import { Component, Input } from "@angular/core";
import { faWrench } from "@fortawesome/free-solid-svg-icons";
import { lastValueFrom } from "rxjs";
import LocalizationConstants from "src/app/constants/localization-constants";
import { ContractToolFilter } from "src/app/models/ContractToolFilter";
import { ExcelExport, ExcelExportType } from "src/app/models/ExcelExport";
import { ToolFilter } from "src/app/models/ToolFilter";
import { CompanyService } from "src/app/services/company.service";
import { ContractService } from "src/app/services/contract.service";
import { FieldServiceService } from "src/app/services/field-service.service";
import { LocalizationService } from "src/app/services/localization.service";
import { QuoteService } from "src/app/services/quote.service";
import { ToolGridService } from "src/app/services/tool-grid.service";
import { DateUtils } from "src/app/utils/date.utils";

import { ToolService } from "../../services/tool.service";
import { FileService } from "./../../services/file.service";

@Component({
  selector: "tool-export-button",
  templateUrl: "./tool-export-button.component.html",
  styleUrls: ["./tool-export-button.component.scss"],
  standalone: false,
})
export class ToolExportButtonComponent {
  faWrench = faWrench;
  readonly localizationConstants = LocalizationConstants;

  @Input() toolFilter: ToolFilter | ContractToolFilter;
  @Input() excelExportType: ExcelExportType;

  constructor(
    private toolService: ToolService,
    private toolGridService: ToolGridService,
    private fileService: FileService,
    private localizationService: LocalizationService,
    private fieldService: FieldServiceService,
    private companyService: CompanyService,
    private contractService: ContractService,
    private quoteService: QuoteService,
  ) {}

  public async onExportToExcel(): Promise<void> {
    switch (this.excelExportType) {
      case ExcelExportType.Tools:
        await this.exportToolAsync();
        break;

      case ExcelExportType.FieldService:
        await this.exportFieldServiceAsync();
        break;

      case ExcelExportType.Contract:
        await this.exportContractAsync();
        break;

      case ExcelExportType.ContractTools:
        await this.exportContractToolsAsync();
        break;
    }
  }

  private async exportToolAsync(): Promise<void> {
    const columns = await this.toolGridService.getAllColumnsForExcelExport();
    const excelExport = new ExcelExport(
      this.localizationService.currentLanguage.code,
      this.toolFilter,
      columns,
    );

    const data = await this.toolService.exportToExcel(excelExport).toPromise();
    const fileName = `Tools_${DateUtils.getStringDate(new Date())}.xlsx`;
    this.fileService.downloadFile(data, fileName);
  }

  private async exportFieldServiceAsync(): Promise<void> {
    const columns = await this.fieldService.getAllColumnsForExcelExport();
    const companyId = this.companyService.selectedCompany.name;

    const data = await this.fieldService.exportToExcel(companyId, columns).toPromise();
    const fileName = `Export_${DateUtils.getStringDate(new Date())}.xlsx`;
    this.fileService.downloadFile(data, fileName);
  }

  private async exportContractAsync(): Promise<void> {
    const columns = await this.contractService.getAllColumnsForExcelExport();
    const companyId = this.companyService.selectedCompany.name;

    const data = await this.contractService.exportToExcel(companyId, columns).toPromise();
    const fileName = `Export_${DateUtils.getStringDate(new Date())}.xlsx`;
    this.fileService.downloadFile(data, fileName);
  }

  private async exportContractToolsAsync(): Promise<void> {
    const columns = await this.contractService.getContractToolsColumnsForExcelExport();

    const excelExport = new ExcelExport(
      this.localizationService.currentLanguage.code,
      this.toolFilter,
      columns,
    );

    const fileContent = await lastValueFrom(this.quoteService.exportToExcelAsync(excelExport));
    const fileName = `Export_${DateUtils.getStringDate(new Date())}.xlsx`;
    this.fileService.downloadFile(fileContent, fileName);
  }
}
